import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Archive, ArchiveRestore, ArrowLeft, Plus, Star } from 'lucide-react';
import { PROJECT_COLOURS, PROJECT_HEALTHS, PROJECT_STATUSES, archiveProject, restoreProject, setProjectFavourite, updateProject } from '@mkcore/projects/schema';
import { Badge, Button, Card, Checkbox, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Textarea, Th, Tr, cn } from '@mkcore/ui';
import { AttachmentsPanel } from '@/features/files/attachments-panel';
import { FolderPanel } from '@/features/files/folder-panel';
import { ProjectMembers } from '@/features/projects/members';
import { ProjectTasks } from '@/features/projects/tasks';
import { ProjectMilestones } from '@/features/projects/milestones';
import { ChangeRequests } from '@/features/projects/change-requests';
import { ProjectStatusUpdates } from '@/features/projects/status-updates';
import { ApiError } from '@/lib/api';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { projectQuery, projectsQuery } from '@/features/projects/queries';
import { ColourDot, HealthBadge, ProjectStatusBadge } from '@/features/projects/labels';
import { NewProjectDialog } from '@/features/projects/new-project';
import { LinkSubproject } from '@/features/projects/link-subproject';
import { toFormErrors } from '@/features/projects/form-errors';
import { organisationsQuery } from '@/features/crm/queries';
import { engagementsQuery } from '@/features/engagements/queries';
import { hoursToMinutes, inputToCents, centsToInput, minutesToHours } from '@/features/engagements/money';
import { ConfirmButton } from '@/components/confirm';
import { LinksPanel } from '@/components/links-panel';
import { RelatedKnowledge } from '@/features/knowledge/panels';
import { formatDate } from '@/lib/format';

export const Route = createFileRoute('/_app/projects/$id')({ component: ProjectDetail });

type Form = {
  organisationId: string;
  engagementId: string;
  parentId: string;
  name: string;
  code: string;
  colour: string;
  status: (typeof PROJECT_STATUSES)[number];
  billable: 'inherit' | 'yes' | 'no';
  startsOn: string;
  endsOn: string;
  budgetHours: string;
  budgetAmount: string;
  notes: string;
  health: '' | (typeof PROJECT_HEALTHS)[number];
  healthNote: string;
  isTemplate: boolean;
};

/** One project: fields, colour, status, budget and its subprojects, also linked afterwards (CO-0189, CO-1268, CO-0193, CO-0195, #115). */
function ProjectDetail() {
  const { t } = useTranslation();
  const canManageMembers = useCan('admin:create');
  const { id } = Route.useParams();
  const message = useErrorMessage();
  const project = useQuery(projectQuery(id));
  const canUpdate = useCan('projects:update');
  const canCreate = useCan('projects:create');
  const [form, setForm] = useState<Form | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newSub, setNewSub] = useState(false);
  useEffect(() => setForm(null), [id, project.data?.version]);
  const invalidate = [['project']];
  const save = useActionMutation(updateProject, { invalidate, success: t('common.saved'), silentErrors: true });
  const archive = useActionMutation(archiveProject, { invalidate, success: t('projects.archivedDone') });
  const restore = useActionMutation(restoreProject, { invalidate, success: t('projects.restoredDone') });
  const favourite = useActionMutation(setProjectFavourite, { invalidate });
  const subs = useInfiniteQuery({ ...projectsQuery({ parentId: id, includeArchived: true, limit: 100 }), enabled: !!project.data && !project.data.parentId });
  // Projects of their own of the same organisation: main projects to link this one under, or projects to link under it (#115).
  const org = project.data?.organisationId;
  const template = !!project.data?.isTemplate;
  const loose = useInfiniteQuery({ ...projectsQuery({ topLevel: true, ...(org && { organisationId: org }), ...(template && { template }), limit: 200 }), enabled: !!project.data && canUpdate });
  // A project of its own can move to another organisation and engagement (#181); a subproject follows its main project.
  const movable = !!project.data && canUpdate && !project.data.parentId;
  const chosenOrg = form ? form.organisationId : (org ?? '');
  const orgs = useInfiniteQuery({ ...organisationsQuery({ limit: 200 }), enabled: movable });
  const engagements = useInfiniteQuery({ ...engagementsQuery({ organisationId: chosenOrg || undefined, limit: 200 }), enabled: movable && !!chosenOrg });

  if (project.isError) return <ErrorState title={t('errors.generic')} description={message(project.error)} />;
  if (!project.data) return <Skeleton className="h-96" />;
  const p = project.data;
  const locked = !canUpdate || !!p.archivedAt;
  const f: Form = form ?? {
    organisationId: p.organisationId ?? '',
    engagementId: p.engagementId ?? '',
    parentId: p.parentId ?? '',
    name: p.name,
    code: p.code ?? '',
    colour: p.colour,
    status: p.status,
    billable: p.billableDefault === null ? 'inherit' : p.billableDefault ? 'yes' : 'no',
    startsOn: p.startsOn ?? '',
    endsOn: p.endsOn ?? '',
    budgetHours: minutesToHours(p.budgetMinutes),
    budgetAmount: centsToInput(p.budgetCents),
    notes: p.notes,
    health: p.health ?? '',
    healthNote: p.healthNote,
    isTemplate: p.isTemplate,
  };
  const set = (patch: Partial<Form>) => setForm({ ...f, ...patch });
  const text = (k: { [K in keyof Form]: Form[K] extends string ? K : never }[keyof Form], label: string, extra: { type?: string; hint?: string } = {}) => (
    <Field label={label} hint={extra.hint} error={errors[k]}>
      {(pr) => <Input {...pr} type={extra.type} value={f[k]} disabled={locked} onChange={(ev) => set({ [k]: ev.target.value } as Partial<Form>)} />}
    </Field>
  );
  const submit = () =>
    save.mutate(
      {
        input: {
          id,
          ...(!p.parentId && { organisationId: f.organisationId || null, engagementId: f.engagementId || null }),
          parentId: f.parentId || null,
          name: f.name,
          code: f.code.trim() || null,
          colour: f.colour,
          status: f.status,
          billableDefault: f.billable === 'inherit' ? null : f.billable === 'yes',
          startsOn: f.startsOn || null,
          endsOn: f.endsOn || null,
          budgetMinutes: hoursToMinutes(f.budgetHours),
          budgetCents: inputToCents(f.budgetAmount),
          notes: f.notes,
          // Only a change of the health sends it: saving it again would move its date.
          ...((f.health || null) !== p.health && { health: f.health || null }),
          ...(f.healthNote !== p.healthNote && { healthNote: f.healthNote }),
          ...(!p.parentId && { isTemplate: f.isTemplate }),
        },
        ifMatch: p.version,
      },
      { onError: (e) => setErrors(e instanceof ApiError ? { ...toFormErrors(e.fieldErrors()), _: message(e) } : { _: message(e) }), onSuccess: () => setErrors({}) },
    );
  const subRows = subs.data?.pages.flatMap((pg) => pg.items) ?? [];
  const looseRows = (loose.data?.pages.flatMap((pg) => pg.items) ?? []).filter((x) => x.id !== id && x.organisationId === p.organisationId);
  const linkable = looseRows.filter((x) => !x.subprojectCount);

  return (
    <>
      <Link to="/projects" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('projects.title')}
      </Link>
      <PageHeader
        eyebrow={
          <span className="flex flex-wrap gap-2">
            {p.organisationId ? (
              <Link to="/org/$id" params={{ id: p.organisationId }} className="hover:underline">
                {p.organisationName}
              </Link>
            ) : (
              t('projects.internal')
            )}
            {p.engagementId && (
              <>
                <span>·</span>
                <Link to="/engagements/$id" params={{ id: p.engagementId }} className="hover:underline">
                  {p.engagementName}
                </Link>
              </>
            )}
            {p.parentId && (
              <>
                <span>·</span>
                <Link to="/projects/$id" params={{ id: p.parentId }} className="hover:underline">
                  {t('projects.parent')}
                </Link>
              </>
            )}
          </span>
        }
        title={
          <span className="flex items-center gap-2">
            <ColourDot colour={p.colour} className="size-3.5" /> {p.name}
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-2">
            <ProjectStatusBadge status={p.status} />
            {p.health && <HealthBadge health={p.health} note={p.healthNote} />}
            {p.isTemplate && <Badge tone="info">{t('projects.template')}</Badge>}
            {p.code && <Badge tone="outline">{p.code}</Badge>}
            <span className="text-fg-3">{p.effectiveBillableDefault ? t('projects.billableYes') : t('projects.billableNo')}</span>
            {p.archivedAt && <Badge tone="neutral">{t('org.archived')}</Badge>}
          </span>
        }
        actions={
          <>
            <Button variant="ghost" size="icon" aria-label={p.favourite ? t('projects.unfavourite') : t('projects.favourite')} aria-pressed={p.favourite} onClick={() => favourite.mutate({ input: { id, favourite: !p.favourite } })}>
              <Star className={p.favourite ? 'text-gold' : undefined} fill={p.favourite ? 'currentColor' : 'none'} />
            </Button>
            {canUpdate &&
              (p.archivedAt ? (
                <Button variant="secondary" loading={restore.isPending} onClick={() => restore.mutate({ input: { id } })}>
                  <ArchiveRestore /> {t('org.restore')}
                </Button>
              ) : (
                <ConfirmButton icon={<Archive />} label={t('org.archive')} title={t('projects.archiveTitle')} description={t('projects.archiveText')} onConfirm={() => archive.mutateAsync({ input: { id } })} />
              ))}
          </>
        }
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card className="grid content-start gap-3 p-4">
          <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
            {text('name', t('common.name'))}
            {text('code', t('projects.fields.code'), { hint: t('projects.fields.codeHint') })}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('common.status')} hint={t('projects.fields.statusHint')}>
              {(pr) => (
                <NativeSelect {...pr} value={f.status} disabled={locked} onChange={(ev) => set({ status: ev.target.value as Form['status'] })}>
                  {PROJECT_STATUSES.map((v) => (
                    <option key={v} value={v}>
                      {t(`projects.status.${v}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('projects.fields.billableDefault')} hint={t('projects.fields.billableHint')}>
              {(pr) => (
                <NativeSelect {...pr} value={f.billable} disabled={locked} onChange={(ev) => set({ billable: ev.target.value as Form['billable'] })}>
                  <option value="inherit">{t('projects.fields.billableInherit')}</option>
                  <option value="yes">{t('common.yes')}</option>
                  <option value="no">{t('common.no')}</option>
                </NativeSelect>
              )}
            </Field>
          </div>
          {!p.parentId && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('org.eyebrow')} hint={t('projects.fields.organisationMoveHint')} error={errors.organisationId}>
                {(pr) => (
                  <NativeSelect {...pr} value={f.organisationId} disabled={locked} onChange={(ev) => set({ organisationId: ev.target.value, engagementId: '' })}>
                    <option value="">{t('projects.internal')}</option>
                    {p.organisationId && !(orgs.data?.pages ?? []).some((pg) => pg.items.some((o) => o.id === p.organisationId)) && <option value={p.organisationId}>{p.organisationName}</option>}
                    {(orgs.data?.pages.flatMap((pg) => pg.items) ?? []).map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <Field label={t('nav.engagements')} error={errors.engagementId}>
                {(pr) => (
                  <NativeSelect {...pr} value={f.engagementId} disabled={locked || !f.organisationId} onChange={(ev) => set({ engagementId: ev.target.value })}>
                    <option value="">–</option>
                    {p.engagementId && f.engagementId === p.engagementId && !(engagements.data?.pages ?? []).some((pg) => pg.items.some((e) => e.id === p.engagementId)) && (
                      <option value={p.engagementId}>{p.engagementName}</option>
                    )}
                    {(engagements.data?.pages.flatMap((pg) => pg.items) ?? []).map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            </div>
          )}
          <Field label={t('projects.fields.parent')} hint={p.subprojectCount ? t('projects.fields.parentLocked') : t('projects.fields.parentHint')} error={errors.parentId}>
            {(pr) => (
              <NativeSelect {...pr} value={f.parentId} disabled={locked || p.subprojectCount > 0} onChange={(ev) => set({ parentId: ev.target.value })}>
                <option value="">{t('projects.fields.parentNone')}</option>
                {p.parentId && !looseRows.some((x) => x.id === p.parentId) && <option value={p.parentId}>{t('projects.parent')}</option>}
                {looseRows.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <div>
            <div className="mb-1 text-sm font-bold">{t('projects.fields.colour')}</div>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('projects.fields.colour')}>
              {PROJECT_COLOURS.map((c) => (
                <button key={c} type="button" role="radio" aria-checked={f.colour === c} aria-label={c} disabled={locked} className={cn('size-7 rounded-full border-2', f.colour === c ? 'border-fg-1' : 'border-transparent')} style={{ backgroundColor: c }} onClick={() => set({ colour: c })} />
              ))}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {text('startsOn', t('engagements.fields.startsOn'), { type: 'date' })}
            {text('endsOn', t('engagements.fields.endsOn'), { type: 'date' })}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {text('budgetHours', t('engagements.fields.budgetHours'))}
            {text('budgetAmount', t('engagements.fields.budgetAmount'))}
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
            <Field label={t('projects.fields.health')} hint={p.healthAt ? t('projects.fields.healthAt', { date: formatDate(p.healthAt) }) : t('projects.fields.healthHint')}>
              {(pr) => (
                <NativeSelect {...pr} value={f.health} disabled={locked} onChange={(ev) => set({ health: ev.target.value as Form['health'] })}>
                  <option value="">{t('projects.health.none')}</option>
                  {PROJECT_HEALTHS.map((v) => (
                    <option key={v} value={v}>
                      {t(`projects.health.${v}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            {text('healthNote', t('projects.fields.healthNote'))}
          </div>
          <Field label={t('org.fields.notes')}>{(pr) => <Textarea {...pr} rows={3} value={f.notes} disabled={locked} onChange={(ev) => set({ notes: ev.target.value })} />}</Field>
          {!p.parentId && (
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={f.isTemplate} disabled={locked} onCheckedChange={(v) => set({ isTemplate: v === true })} />
              <span>
                {t('projects.fields.isTemplate')}
                <span className="block text-xs text-fg-3">{t('projects.fields.isTemplateHint')}</span>
              </span>
            </label>
          )}
          {errors._ && (
            <p role="alert" className="text-sm text-danger">
              {errors._}
            </p>
          )}
          {!locked && (
            <div className="flex justify-end">
              <Button disabled={!form} loading={save.isPending} onClick={submit}>
                {t('common.save')}
              </Button>
            </div>
          )}
        </Card>
        {!p.parentId && (
          <Card className="grid content-start gap-3 p-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-lg font-bold">{t('projects.subprojects')}</h2>
              {canCreate && !p.archivedAt && (
                <Button size="sm" variant="secondary" onClick={() => setNewSub(true)}>
                  <Plus /> {t('projects.newSubShort')}
                </Button>
              )}
            </div>
            <p className="text-xs text-fg-3">{t('projects.subprojectsHint')}</p>
            {subs.isLoading ? (
              <Skeleton className="h-20" />
            ) : !subRows.length ? (
              <EmptyState title={t('projects.noSubprojects')} />
            ) : (
              <Table>
                <THead>
                  <Tr>
                    <Th>{t('common.name')}</Th>
                    <Th>{t('common.status')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {subRows.map((s) => (
                    <Tr key={s.id}>
                      <Td>
                        <Link to="/projects/$id" params={{ id: s.id }} className="font-bold hover:underline">
                          {s.name}
                        </Link>
                        {s.archivedAt && (
                          <Badge tone="neutral" className="ml-2">
                            {t('org.archived')}
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <ProjectStatusBadge status={s.status} />
                      </Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            )}
            {canUpdate && !p.archivedAt && linkable.length > 0 && <LinkSubproject parentId={id} candidates={linkable} />}
            <NewProjectDialog open={newSub} onClose={() => setNewSub(false)} parentId={id} parentName={p.name} />
          </Card>
        )}
        <Card className="p-4">
          <ProjectMilestones projectId={id} archived={!!p.archivedAt} />
        </Card>
        <Card className="p-4">
          <ProjectTasks projectId={id} archived={!!p.archivedAt} />
        </Card>
        <Card className="p-4">
          <ChangeRequests projectId={id} archived={!!p.archivedAt} />
        </Card>
        <Card className="p-4">
          <ProjectStatusUpdates projectId={id} archived={!!p.archivedAt} />
        </Card>
        <LinksPanel links={p.links} editable={canUpdate && !p.archivedAt} onSave={(links) => save.mutateAsync({ input: { id, links }, ifMatch: p.version })} />
        <ProjectMembers projectId={id} canManage={canManageMembers && !p.archivedAt} />
        <AttachmentsPanel type="project" id={id} archived={!!p.archivedAt} />
        <FolderPanel type="project" id={id} archived={!!p.archivedAt} />
        <RelatedKnowledge type="project" id={id} q={p.name} />
      </div>
    </>
  );
}
